import { useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { RequireCapability } from "@/components/RequireCapability";
import { RequireSubscription } from "@/components/RequireSubscription";

import { AuthoringFlow } from "./AuthoringFlow";
import { ReviewFlow } from "./components/ReviewFlow";

/**
 * Enterprise communication authoring: the member's 4-step wizard (profile →
 * issues → actions → preview/submit). The validator review flow lives on its
 * own route (`/communication-review`, in the header Management menu) rather than
 * as a toggle here. The public `/communication/<slug>/` page stays
 * Django-rendered.
 */
export function CommunicationsPage() {
  const { t } = useTranslation();

  return (
    <RequireCapability required={["authenticated"]}>
      <RequireSubscription>
        <div className="space-y-6">
          <h1 className="text-3xl font-bold">{t("communications.title")}</h1>
          <AuthoringFlow />
        </div>
      </RequireSubscription>
    </RequireCapability>
  );
}

/**
 * Validator communication review, reached from the header Management menu (only
 * users with the `communications` capability — i.e. validators — see the link).
 * A member link (`?review=<profileId>`) still deep-links straight to a specific
 * review.
 */
export function CommunicationReviewPage() {
  const { t } = useTranslation();

  // The route validates `?review` (communicationReviewRoute); read it loosely
  // here to avoid importing the route (which imports this component).
  const search = useSearch({ strict: false }) as { review?: unknown };
  const reviewId = typeof search.review === "string" ? search.review : "";

  return (
    <RequireCapability required={["communications"]}>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">{t("nav.communicationReview")}</h1>
        <ReviewFlow initialProfileId={reviewId} />
      </div>
    </RequireCapability>
  );
}
